import { useAuthStore } from "@/stores/auth";
import { RBAC_MATRIX, type RbacAction } from "@/lib/rbacMatrix";
import type { UUID } from "@/api/types";

export interface OwnContext {
  /** Whether the meeting is "own" for the current user (FR §3 SPEC). */
  uploadedBy?: UUID | null;
  participants?: readonly UUID[];
}

function isOwnMeeting(
  userId: UUID,
  employeeId: UUID | null,
  ctx: OwnContext,
): boolean {
  if (ctx.uploadedBy && ctx.uploadedBy === userId) return true;
  if (employeeId && ctx.participants?.includes(employeeId)) return true;
  return false;
}

/**
 * Client-side RBAC gate. Always defer to backend 403 for true enforcement
 * (SPEC §3; UI_PLAN §10) — this hook is for hiding inaccessible controls.
 */
export function useCan(action: RbacAction, ctx?: OwnContext): boolean {
  const role = useAuthStore((s) => s.role);
  const isActive = useAuthStore((s) => s.isActive);
  const userId = useAuthStore((s) => s.userId);
  const employeeId = useAuthStore((s) => s.employeeId);
  if (!role || !isActive || !userId) return false;
  const decision = RBAC_MATRIX[action][role];
  if (decision === "allow") return true;
  if (decision === "deny") return false;
  return isOwnMeeting(userId, employeeId, ctx ?? {});
}
